import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Chip,
  Box,
  IconButton,
  Tooltip,
  Icon
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import DateFilterPresets from 'components/DateFilterPresets';
import { getAutocompleteClients } from 'services/clients';
import { getAutocompleteOperators } from 'services/operators';
import { getAutocompleteBrands } from 'services/brands';
import debounce from 'lodash.debounce';

const SECTION_LABEL_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.75,
  mb: 1
};

const SELECT_SX = {
  height: 38,
  borderRadius: '8px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderRadius: '8px'
  }
};

const CHIP_SX = {
  height: 20,
  fontSize: '0.68rem',
  fontWeight: 600,
  borderRadius: '6px'
};

function UnifiedFilterBar({
  filters,
  setDateRange,
  setEntityFilters,
  pageFilters = {},
  setPageFilters,
  pageFilterConfig = [],
  onClearPageFilters,
  showEntityFilters = true
}) {
  const [clients, setClients] = useState([]);
  const [operators, setOperators] = useState([]);
  const [brands, setBrands] = useState([]);

  // Local state for text inputs (debounced)
  const [localInputs, setLocalInputs] = useState({});

  // Initialize local inputs from pageFilters
  useEffect(() => {
    const textInputs = {};
    pageFilterConfig.forEach((config) => {
      if (config.type === 'text') {
        textInputs[config.key] = pageFilters[config.key] || '';
      }
    });
    setLocalInputs(textInputs);
  }, []);

  // Load entity data
  useEffect(() => {
    const loadEntities = async () => {
      try {
        const [clientsData, operatorsData, brandsData] = await Promise.all([
          getAutocompleteClients(''),
          getAutocompleteOperators(''),
          getAutocompleteBrands('')
        ]);
        setClients(Array.isArray(clientsData) ? clientsData : []);
        setOperators(Array.isArray(operatorsData) ? operatorsData : []);
        setBrands(Array.isArray(brandsData) ? brandsData : []);
      } catch (err) {
        console.error('Error loading entities:', err);
      }
    };

    if (showEntityFilters) {
      loadEntities();
    }
  }, [showEntityFilters]);

  // Debounced text input handler
  const debouncedSetPageFilter = useMemo(
    () =>
      debounce((key, value) => {
        setPageFilters((prev) => ({ ...prev, [key]: value }));
      }, 500),
    [setPageFilters]
  );

  const handleDateChange = (startDate, endDate, preset) => {
    setDateRange(startDate, endDate, preset);
  };

  const handleClientChange = (event) => {
    const selectedIds = event.target.value;
    const selectedNames = selectedIds.map((id) => clients.find((c) => c.value === id)?.label).filter(Boolean);
    setEntityFilters({
      clientIds: selectedIds,
      clientNames: selectedNames
    });
  };

  const handleOperatorChange = (event) => {
    const selectedIds = event.target.value;
    const selectedNames = selectedIds.map((id) => operators.find((o) => o.value === id)?.label).filter(Boolean);
    setEntityFilters({
      operatorIds: selectedIds,
      operatorNames: selectedNames
    });
  };

  const handleBrandChange = (event) => {
    const selectedIds = event.target.value;
    const selectedNames = selectedIds.map((id) => brands.find((b) => b.value === id)?.label).filter(Boolean);
    setEntityFilters({
      brandIds: selectedIds,
      brandNames: selectedNames
    });
  };

  const handleTextInputChange = (key, value) => {
    setLocalInputs((prev) => ({ ...prev, [key]: value }));
    debouncedSetPageFilter(key, value);
  };

  const handleSelectChange = (key, value) => {
    setPageFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearPageFilters = () => {
    const clearedInputs = {};
    pageFilterConfig.forEach((config) => {
      if (config.type === 'text') {
        clearedInputs[config.key] = '';
      }
    });
    setLocalInputs(clearedInputs);

    if (onClearPageFilters) {
      onClearPageFilters();
    }
  };

  const renderPageFilter = (config) => {
    switch (config.type) {
      case 'text':
        return (
          <MDInput
            key={config.key}
            label={config.label}
            value={localInputs[config.key] || ''}
            onChange={(e) => handleTextInputChange(config.key, e.target.value)}
            size='small'
            sx={{ minWidth: 160, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
          />
        );

      case 'select':
        return (
          <FormControl key={config.key} size='small' sx={{ minWidth: 140 }}>
            <InputLabel>{config.label}</InputLabel>
            <Select
              value={pageFilters[config.key] || ''}
              onChange={(e) => handleSelectChange(config.key, e.target.value)}
              label={config.label}
              sx={SELECT_SX}
            >
              <MenuItem value=''>
                <em>All</em>
              </MenuItem>
              {(config.options || []).map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'async-select':
        return (
          <FormControl key={config.key} size='small' sx={{ minWidth: 140 }}>
            <InputLabel>{config.label}</InputLabel>
            <Select
              value={pageFilters[config.key] || ''}
              onChange={(e) => handleSelectChange(config.key, e.target.value)}
              label={config.label}
              sx={SELECT_SX}
            >
              <MenuItem value=''>
                <em>All</em>
              </MenuItem>
              {(config.loadedOptions || []).map((opt) => (
                <MenuItem key={opt.value || opt.id || opt} value={opt.value || opt.id || opt}>
                  {opt.label || opt.name || opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      default:
        return null;
    }
  };

  const hasActivePageFilters = pageFilterConfig.some((config) => {
    const value = pageFilters[config.key];
    return value && value !== '';
  });

  const makeRenderValue = (selectedIds, allItems) => {
    if (!selectedIds || selectedIds.length === 0) return <span style={{ opacity: 0.5 }}>All</span>;
    const valid = selectedIds.filter((id) => allItems.find((i) => i.value === id));
    if (valid.length === 0) return <span style={{ opacity: 0.5 }}>All</span>;
    if (valid.length <= 2)
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {valid.map((id) => {
            const item = allItems.find((i) => i.value === id);
            return <Chip key={id} label={item?.label || id} size='small' color='info' variant='outlined' sx={CHIP_SX} />;
          })}
        </Box>
      );
    return <Chip label={`${valid.length} selected`} size='small' color='info' variant='outlined' sx={CHIP_SX} />;
  };

  return (
    <Card sx={{ mb: 2, overflow: 'visible', borderRadius: '12px' }}>
      <MDBox
        sx={{
          height: '3px',
          background: 'linear-gradient(90deg, #e2001b, #ff4f69, transparent 80%)',
          borderRadius: '12px 12px 0 0'
        }}
      />
      <MDBox p={{ xs: 1.5, md: 2.5 }}>
        {/* Row 1: Date Range */}
        <MDBox mb={2.5}>
          <MDBox sx={SECTION_LABEL_SX}>
            <Icon sx={{ fontSize: '1rem', opacity: 0.6 }}>date_range</Icon>
            <MDTypography variant='caption' fontWeight='bold' textTransform='uppercase' sx={{ letterSpacing: '0.06em', opacity: 0.7 }}>
              Date Range
            </MDTypography>
          </MDBox>
          <DateFilterPresets
            onDateChange={handleDateChange}
            preset={filters.preset || 'today'}
            startDate={filters.startDate}
            endDate={filters.endDate}
          />
        </MDBox>

        {/* Row 2: Entity Filters */}
        {showEntityFilters && (
          <MDBox mb={pageFilterConfig.length > 0 ? 2.5 : 0} pt={2} sx={{ borderTop: '1px solid rgba(128,128,128,0.15)' }}>
            <MDBox sx={SECTION_LABEL_SX}>
              <Icon sx={{ fontSize: '1rem', opacity: 0.6 }}>account_tree</Icon>
              <MDTypography variant='caption' fontWeight='bold' textTransform='uppercase' sx={{ letterSpacing: '0.06em', opacity: 0.7 }}>
                Entity Filters
              </MDTypography>
            </MDBox>
            <MDBox display='flex' gap={1.5} flexWrap='wrap' sx={{ '& .MuiFormControl-root': { flex: { xs: '1 1 100%', sm: '0 0 auto' } } }}>
              {/* Integration */}
              <FormControl size='small' sx={{ minWidth: 185 }}>
                <InputLabel shrink>Integration</InputLabel>
                <Select multiple value={filters.clientIds || []} onChange={handleClientChange}
                  label='Integration' notched displayEmpty sx={SELECT_SX}
                  renderValue={(sel) => makeRenderValue(sel, clients)}>
                  {clients.map((c) => (
                    <MenuItem key={c.value} value={c.value}>
                      <Checkbox size='small' checked={(filters.clientIds || []).includes(c.value)} />
                      <ListItemText primary={c.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Operator */}
              <FormControl size='small' sx={{ minWidth: 185 }}>
                <InputLabel shrink>Operator</InputLabel>
                <Select multiple value={filters.operatorIds || []} onChange={handleOperatorChange}
                  label='Operator' notched displayEmpty sx={SELECT_SX}
                  renderValue={(sel) => makeRenderValue(sel, operators)}>
                  {operators.length === 0
                    ? <MenuItem disabled><em>None available</em></MenuItem>
                    : operators.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        <Checkbox size='small' checked={(filters.operatorIds || []).includes(o.value)} />
                        <ListItemText primary={o.label} />
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              {/* Brand */}
              <FormControl size='small' sx={{ minWidth: 185 }}>
                <InputLabel shrink>Brand</InputLabel>
                <Select multiple value={filters.brandIds || []} onChange={handleBrandChange}
                  label='Brand' notched displayEmpty sx={SELECT_SX}
                  renderValue={(sel) => makeRenderValue(sel, brands)}>
                  {brands.map((b) => (
                    <MenuItem key={b.value} value={b.value}>
                      <Checkbox size='small' checked={(filters.brandIds || []).includes(b.value)} />
                      <ListItemText primary={b.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </MDBox>
          </MDBox>
        )}

        {/* Row 3: Page-Specific Filters */}
        {pageFilterConfig.length > 0 && (
          <MDBox pt={2} sx={{ borderTop: '1px solid rgba(128,128,128,0.15)' }}>
            <MDBox sx={{ ...SECTION_LABEL_SX, justifyContent: 'space-between' }}>
              <MDBox sx={SECTION_LABEL_SX}>
                <Icon sx={{ fontSize: '1rem', opacity: 0.6 }}>tune</Icon>
                <MDTypography variant='caption' fontWeight='bold' textTransform='uppercase' sx={{ letterSpacing: '0.06em', opacity: 0.7 }}>
                  Page Filters
                </MDTypography>
              </MDBox>
              {hasActivePageFilters && (
                <Tooltip title='Clear page filters'>
                  <IconButton size='small' onClick={handleClearPageFilters}
                    sx={{
                      borderRadius: '8px', px: 1,
                      color: '#e57373',
                      border: '1px solid rgba(229,115,115,0.35)',
                      '&:hover': { background: 'rgba(229,115,115,0.1)' }
                    }}
                  >
                    <ClearIcon fontSize='small' />
                    <MDTypography variant='caption' sx={{ ml: 0.5, color: 'inherit', fontWeight: 600 }}>Clear</MDTypography>
                  </IconButton>
                </Tooltip>
              )}
            </MDBox>
            <MDBox display='flex' flexWrap='wrap' gap={1.5} alignItems='center' sx={{ '& .MuiFormControl-root, & .MuiTextField-root': { flex: { xs: '1 1 100%', sm: '0 0 auto' } } }}>
              {pageFilterConfig.map((config) => renderPageFilter(config))}
            </MDBox>
          </MDBox>
        )}
      </MDBox>
    </Card>
  );
}

UnifiedFilterBar.propTypes = {
  filters: PropTypes.object.isRequired,
  setDateRange: PropTypes.func.isRequired,
  setEntityFilters: PropTypes.func.isRequired,
  pageFilters: PropTypes.object,
  setPageFilters: PropTypes.func,
  pageFilterConfig: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['text', 'select', 'async-select']).isRequired,
      options: PropTypes.array,
      loadedOptions: PropTypes.array
    })
  ),
  onClearPageFilters: PropTypes.func,
  showEntityFilters: PropTypes.bool
};

export default UnifiedFilterBar;
